import { filter } from 'lodash'
import {
  EsCharacteristicStrategy,
  EsCharacteristicsPromoted,
  type FundType,
  type SfdrStrategyType,
} from '~/types/types'

export const esCharacteristicMapping = {
  [EsCharacteristicStrategy.EsgIntegration]: {
    label: {
      en: 'The fund promotes E/S characteristics by ESG Integration into the investment strategy, e.g. if potential and current investments are required to conduct the ESG Assessment of ImpactNexus as part of the due diligence or regular monitorings.',
      de: 'Der Fonds fördert E-/S-Merkmale durch die Integration von ESG-Aspekten in die Anlagestrategie, z. B. indem potenzielle und aktuelle Investitionen verpflichtet werden, die ESG-Bewertung von ImpactNexus im Rahmen der Due-Diligence-Prüfung oder regelmäßiger Überwachungen durchzuführen.',
    },
    sublabel: {
      en: 'This approach is strongly recommended as minimum approach.',
      de: 'Dieser Ansatz wird nachdrücklich als Mindestvorgehen empfohlen.',
    },
    info: {
      en: 'The explicit inclusion by asset managers of ESG risks and opportunities into traditional financial analysis and investment decisions based on a systematic process and appropriate research sources. This type covers explicit consideration of ESG factors alongside financial factors in the mainstream analysis of investments. The integration process focuses on the potential impact of ESG issues on company financials (positive and negative), which in turn may affect the investment decision.',
      de: 'Die explizite Einbeziehung von ESG-Risiken und -Chancen durch Vermögensverwalter in die traditionelle Finanzanalyse und Anlageentscheidungen, basierend auf einem systematischen Prozess und geeigneten Recherchequellen. Dieser Ansatz umfasst die ausdrückliche Berücksichtigung von ESG-Faktoren neben finanziellen Faktoren in der herkömmlichen Analyse von Investitionen. Der Integrationsprozess konzentriert sich auf die potenziellen Auswirkungen von ESG-Themen auf die Finanzkennzahlen eines Unternehmens (sowohl positiv als auch negativ), was sich wiederum auf die Investitionsentscheidung auswirken kann.',
    },
  },
  [EsCharacteristicStrategy.ExclusionCriteria]: {
    label: {
      en: 'The fund promotes E/S characteristics by the application of exclusion criteria, so called values-based screening.',
      de: 'Der Fonds fördert E-/S-Merkmale durch die Anwendung von Ausschlusskriterien (sogenanntes wertebasiertes Screening).',
    },
    sublabel: {
      en: 'This approach is strongly recommended as minimum approach.',
      de: 'Dieser Ansatz wird nachdrücklich als Mindestvorgehen empfohlen.',
    },
    info: {
      en: 'An approach that excludes specific investments or classes of investment from the investible universe such as companies, sectors, or countries. This approach systematically excludes companies, sectors, or countries from the permissible investment universe if involved in certain activities based on specific criteria. Common criteria include weapons, pornography, tobacco, and animal testing. Exclusions can be applied at an individual fund or mandate level, but increasingly also at an asset manager or an asset owner level, across the entire product range of assets.',
      de: 'Ein Ansatz, der bestimmte Investitionen oder Anlageklassen aus dem investierbaren Universum ausschließt, wie beispielsweise Unternehmen, Branchen oder Länder. Dieser Ansatz schließt systematisch Unternehmen, Branchen oder Länder aus dem zulässigen Anlageuniversum aus, wenn sie anhand bestimmter Kriterien an bestimmten Aktivitäten beteiligt sind. Häufig genutzte Kriterien umfassen Waffen, Pornografie, Tabak und Tierversuche. Ausschlüsse können auf Ebene eines einzelnen Fonds oder Mandats angewandt werden, zunehmend jedoch auch auf Ebene eines Vermögensverwalters oder eines Asset Owners für die gesamte Produktpalette.',
    },
  },
  [EsCharacteristicStrategy.BestInClass]: {
    label: {
      en: 'The fund promotes E/S characteristics by a Best-in-class approach.',
      de: 'Der Fonds fördert E-/S-Merkmale mittels eines Best-in-Class-Ansatzes.',
    },
    sublabel: {
      en: '',
      de: '',
    },
    info: {
      en: 'The fund promotes environmental and/or social characteristics by selecting or weighting the best-performing (in terms of the ESG criteria) companies or assets, within a defined investment universe. (e.g., only investments in companies with high human rights index).',
      de: 'Der Fonds fördert Ökologische und/oder soziale Merkmale, indem er innerhalb eines definierten Anlageuniversums die in Bezug auf ESG-Kriterien leistungsstärksten Unternehmen oder Vermögenswerte auswählt oder gewichtet (z. B. nur Investitionen in Unternehmen mit hohem Menschenrechtsindex).',
    },
  },
  [EsCharacteristicStrategy.NormBasedScreening]: {
    label: {
      en: 'The fund promotes E/S characteristics by a Norms-Based Screening.',
      de: 'Der Fonds fördert E-/S-Merkmale durch ein normenbasiertes Screening.',
    },
    sublabel: {
      en: '',
      de: '',
    },
    info: {
      en: 'The fund excludes companies that violate internationally (or nationally) recognized norms and conventions. This approach involves the screening of investments based on international norms or combinations of norms covering ESG factors. International norms on ESG are those defined by international bodies such as the United Nations (e.g., Rio Declaration on Environment and Development, United Nations Convention against Corruption, etc.). Please note that Article 8 products shall, by default, align with the OECD Guidelines for Multinational Enterprises and the UN Guiding Principles on Business and Human Rights, regardless of the consideration of further norms and conventions. Therefore, if your fund is only aligned with these two international standards, do not mention norms-based screening as an additional ESG strategy your fund applies.',
      de: 'Der Fonds schließt Unternehmen aus, die gegen international (oder national) anerkannte Normen und Konventionen verstoßen. Dieser Ansatz beinhaltet ein Screening von Investitionen anhand internationaler Normen oder Normkombinationen, die ESG-Faktoren betreffen. Internationale ESG-Normen werden von internationalen Institutionen wie den Vereinten Nationen definiert (z. B. Rio-Erklärung über Umwelt und Entwicklung, UN-Konvention gegen Korruption usw.). Bitte beachten Sie, dass Produkte nach Artikel 8 grundsätzlich den OECD-Leitsätzen für multinationale Unternehmen und den UN-Leitprinzipien für Wirtschaft und Menschenrechte entsprechen müssen, unabhängig von der Berücksichtigung weiterer Normen und Konventionen. Wenn Ihr Fonds also nur an diese beiden internationalen Standards gebunden ist, sollten Sie norms-basiertes Screening nicht als zusätzliche ESG-Strategie angeben, die Ihr Fonds anwendet.',
    },
  },
  [EsCharacteristicStrategy.SustainabilityLabel]: {
    label: {
      en: 'The fund promotes E/S characteristics by bearing a Sustainability Label.',
      de: 'Der Fonds fördert E-/S-Merkmale, indem er ein Nachhaltigkeitslabel trägt.',
    },
    sublabel: {
      en: '',
      de: '',
    },
    info: {
      en: 'The fund is labeled as sustainable including being classified as Article 8 or 9 SFDR (e.g., when obtaining the FNG Siegel label).',
      de: 'Der Fonds ist als nachhaltig gekennzeichnet, unter anderem durch die Einstufung als Artikel-8- oder Artikel-9-SFDR-Produkt (z. B. wenn er das FNG-Siegel erhält).',
    },
  },
}

export const esCharacteristicPromotedMapping = {
  [EsCharacteristicsPromoted.OnlyEnvironmental]: {
    label: {
      en: 'Only Environmental characteristics',
      de: 'Ausschließlich Umweltmerkmale',
    },
    sublabel: {
      en: '',
      de: '',
    },
    info: {
      en: '',
      de: '',
    },
  },
  [EsCharacteristicsPromoted.OnlySocial]: {
    label: {
      en: 'Only Social characteristics',
      de: 'Ausschließlich Sozialmerkmale',
    },
    sublabel: {
      en: '',
      de: '',
    },
    info: {
      en: '',
      de: '',
    },
  },
  [EsCharacteristicsPromoted.Both]: {
    label: {
      en: 'Both Environmental and Social characteristics',
      de: 'Sowohl Umwelt- als auch Sozialmerkmale',
    },
    sublabel: {
      en: '',
      de: '',
    },
    info: {
      en: '',
      de: '',
    },
  },
}

export const sustainabilityIndictatorDescription = {
  en: `
Sustainability indicators serve to measure the attainment of
- the sustainable investments objectives of a fund (for article 9 funds)
- the environmental or social characteristics of a fund (for article 8 funds)
- the impact of individual sustainable investments (portfolio companies) in a fund (for article 8+ funds)

Please note that the sustainability indicators and the PAI indicators refer to different disclosures under the SFDR. However, it is recommended to choose the PAI indicators as sustainability indicators to measure the sustainability performance of a fund e.g. by showing improvements of the investments against those indicators over time.
  `,
  de: `
Nachhaltigkeitsindikatoren dienen dazu, Folgendes zu messen:
- die Ziele nachhaltiger Investitionen eines Fonds (für Artikel-9-Fonds)
- die Umwelt- oder Sozialmerkmale eines Fonds (für Artikel-8-Fonds)
- die Auswirkungen einzelner nachhaltiger Investitionen (Portfoliounternehmen) in einem Fonds (für Artikel-8+-Fonds)

Bitte beachten Sie, dass sich Nachhaltigkeitsindikatoren und PAI-Indikatoren auf unterschiedliche Offenlegungen gemäß der SFDR beziehen. Es wird jedoch empfohlen, die PAI-Indikatoren als Nachhaltigkeitsindikatoren zu wählen, um die Nachhaltigkeitsleistung eines Fonds zu messen – etwa indem die Verbesserungen der Investitionen gegenüber diesen Indikatoren im Zeitverlauf aufgezeigt werden.
  `,
}

export const sustainabilityBenchmarkTemplate1 = {
  en: `
*Disclaimer: This statement on benchmark reference for VC and PE funds was created by ImpactNexus. It is intended to provide assistance and guidance. It is not an official statement of the SFDR regulation. ImpactNexus assumes no liability for the accuracy of the content of this statement.*

## General information
The underlying assets of (name of the fund) are committed to decarbonisation of the
economy and have a carbon emission reduction as their sustainable investment objective.
According to the rules set out in [Article 49, points (1a) and (1b) of the Sustainable Finance Disclosure Regulation (SFDR)](https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:32022R1288&from=ET#:~:text=1.%C2%A0%C2%A0%C2%A0In%20the%20website%20section%20%E2%80%98Attainment,of%20those%20benchmarks%20can%20be%20found), financial market participants holding a financial product with
the objective of a carbon emission reduction shall describe, which market index has been
designated as a reference benchmark and how this index is aligned with the sustainable
investment objective. The reference benchmark shall qualify as an EU Climate Transition
Benchmark or an EU Paris-aligned Benchmark as defined in Article 3, points (23a) and (23b),
of Regulation (EU) 2016/1011. As the asset class of (name of the fund) is (Private
Equity/Venture Capital/Other(specify)), there are currently no market indexes, which can
satisfy the above criteria for a reference benchmark.
In this case, under [Article 49, point (3) of the SFDR](https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:32022R1288&from=ET#:~:text=to%20that%20information.-,3.%C2%A0%C2%A0%C2%A0By%20way%20of%20derogation%20from%20paragraph%201,-%2C%20point%20(b)%2C%20where), the financial market participants
shall explain how the continued effort of attaining the objective of reducing carbon emissions
is ensured in view of achieving the objectives of the Paris Agreement. Namely, the extent to
which the financial product complies with the methodological requirements set out in
[Delegated Regulation (EU) 2020/1818](https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=CELEX:32020R1818)[^1]. The effort of (name of the fund) to satisfy the
methodological requirements is described in the statement below:
`,
  de: `
*Haftungsausschluss: Diese Erklärung zum Referenzwert für VC- und PE-Fonds wurde von ImpactNexus erstellt. Sie dient zu Unterstützungs- und Orientierungszwecken. Sie ist keine offizielle Stellungnahme zur SFDR-Verordnung. ImpactNexus übernimmt keine Haftung für die Richtigkeit der Inhalte dieser Erklärung.*

## Allgemeine Informationen
Die zugrunde liegenden Vermögenswerte des (Name des Fonds) sind der Dekarbonisierung der
Wirtschaft verpflichtet und verfolgen eine Reduzierung von Kohlenstoffemissionen als nachhaltiges
Anlageziel. Gemäß den Vorgaben in [Artikel 49, Punkte (1a) und (1b) der Verordnung über nachhaltigkeitsbezogene Offenlegungspflichten (SFDR)](https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32022R1288&from=DE#:~:text=1.%C2%A0%C2%A0%C2%A0In%20der%20Website-Rubrik%20%E2%80%9EErreichung,der%20jeweiligen%20Referenzindizes%20zu%20finden%20sind), müssen Finanzmarktteilnehmer, die ein Finanzprodukt mit
dem Ziel einer Verringerung von Kohlenstoffemissionen halten, darlegen, welcher Marktindex als
Referenzwert festgelegt wurde und wie dieser Index mit dem nachhaltigen Anlageziel im Einklang
steht. Der Referenzwert muss den Anforderungen eines EU-Climate-Transition-Benchmarks oder
eines EU-Paris-aligned-Benchmarks gemäß Artikel 3, Punkte (23a) und (23b) der Verordnung (EU)
2016/1011 entsprechen. Da es sich bei der Anlageklasse des (Name des Fonds) um (Private
Equity/Venture Capital/Andere (genau angeben)) handelt, gibt es derzeit keine Marktindizes,
die diese Kriterien für einen Referenzwert erfüllen können.
In diesem Fall müssen gemäß [Artikel 49, Punkt (3) der SFDR](https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32022R1288&from=DE) die Finanzmarktteilnehmer
darlegen, wie sie den kontinuierlichen Beitrag zur Erreichung des Ziels der Emissionsreduktion im
Hinblick auf die Ziele des Übereinkommens von Paris sicherstellen. Konkret ist anzugeben, inwieweit
das Finanzprodukt den in der [Delegierten Verordnung (EU) 2020/1818](https://eur-lex.europa.eu/legal-content/DE/TXT/PDF/?uri=CELEX:32020R1818)[^1] festgelegten
methodischen Anforderungen entspricht. Die Bemühungen des (Name des Fonds), diese
methodischen Anforderungen zu erfüllen, werden in der folgenden Erklärung beschrieben:
`,
}

export const sustainabilityBenchmarkTemplateStatement = {
  en: `> The underlying assets of <em><strong>(name of the fund)</strong></em> are committed to decarbonisation of the economy
and have a carbon emission reduction as their sustainable investment objective. The fund’s
objective adheres to the 1,5 °C scenario, with no or limited overshoot, referred to in the Special
Report on Global Warming of 1,5 °C from the Intergovernmental Panel on Climate Change
(IPCC) as the reference temperature scenario. Because there are no market indexes, which
can be used as a reference benchmark for <em><strong>(name of the fund)</em></strong>, the present statement contains
the description of a continued effort of attaining the objective of reducing carbon emissions in
line with the requirements set out on the SFDR. Therefore, the weighted average GHG
intensity[^2] of the fund, including Scope 1, 2, and 3 GHG emissions, is <em><strong>_____ %</strong></em> lower than the GHG
intensity of the investable universe[^3]. Moreover, the fund maintains a <em><strong>_____ %</strong></em> reduction of GHG
intensity on average per annum[^4]. Finally, the fund applies the following exclusion criteria when
making investment decisions: <em><strong>_____</strong></em>. Therefore, the financial product <em><strong>(name of the fund)</strong></em>
complies/does not yet fully comply with the [methodological requirements set out in Delegated Regulation (EU) 2020/1818](https://eur-lex.europa.eu/legal-content/EN/ALL/?uri=CELEX%3A32020R1818#:~:text=CHAPTER%20II,THE%20BENCHMARK%20METHODOLOGY). In case the fund does not yet comply with all the requirements,
please, explain the reasons for it and describe the measures taken with a view to ensuring
compliance with the methodological requirements.`,
  de: `> Die zugrunde liegenden Vermögenswerte von <em><strong>(Name des Fonds)</strong></em> sind der Dekarbonisierung
der Wirtschaft verpflichtet und verfolgen als nachhaltiges Anlageziel eine Verringerung der
Kohlenstoffemissionen. Das Ziel des Fonds richtet sich nach dem 1,5°C-Szenario, mit keinem
oder nur geringem Überschreiten, das im Sonderbericht über die globale Erwärmung um 1,5°C
des Zwischenstaatlichen Ausschusses für Klimaänderungen (IPCC) als Referenzszenario genannt wird.
Da es keine Marktindizes gibt, die als Referenzwert für <em><strong>(Name des Fonds)</strong></em> genutzt werden
können, enthält diese Erklärung eine Beschreibung des anhaltenden Engagements zur Erreichung
des Ziels der Emissionsreduktion im Einklang mit den Anforderungen der SFDR. Die gewichtete
durchschnittliche Treibhausgasintensität[^2] des Fonds (einschließlich Scope-1-, Scope-2- und
Scope-3-Emissionen) ist um <em><strong>_____ %</strong></em> geringer als die Treibhausgasintensität des investierbaren
Universums[^3]. Darüber hinaus erzielt der Fonds eine durchschnittliche jährliche Verringerung
der Treibhausgasintensität um <em><strong>_____ %</strong></em>[^4]. Schließlich wendet der Fonds bei seinen
Investitionsentscheidungen folgende Ausschlusskriterien an: <em><strong>_____</strong></em>. Daher erfüllt das
Finanzprodukt <em><strong>(Name des Fonds)</strong></em> die [methodischen Anforderungen der Delegierten Verordnung (EU) 2020/1818](https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=CELEX%3A32020R1818#:~:text=CHAPTER%20II,THE%20BENCHMARK%20METHODOLOGY)
vollständig/noch nicht vollständig. Falls der Fonds noch nicht alle Anforderungen erfüllt, erläutern
Sie bitte die Gründe dafür und beschreiben Sie die Maßnahmen, die ergriffen wurden, um die
Einhaltung der methodischen Anforderungen zu gewährleisten.`,
}

export const sustainabilityBenchmarkTemplateStatementCopied = {
  en: `The underlying assets of _________ (name of the fund) are committed to decarbonisation of the economy
and have a carbon emission reduction as their sustainable investment objective. The fund’s
objective adheres to the 1,5 °C scenario, with no or limited overshoot, referred to in the Special
Report on Global Warming of 1,5 °C from the Intergovernmental Panel on Climate Change
(IPCC) as the reference temperature scenario. Because there are no market indexes, which
can be used as a reference benchmark for _________ (name of the fund), the present statement contains
the description of a continued effort of attaining the objective of reducing carbon emissions in
line with the requirements set out on the SFDR. Therefore, the weighted average GHG
intensity of the fund, including Scope 1, 2, and 3 GHG emissions, is _________ % lower than the GHG
intensity of the investable universe. Moreover, the fund maintains a _________ % reduction of GHG
intensity on average per annum. Finally, the fund applies the following exclusion criteria when
making investment decisions: _________. Therefore, the financial product _________ (name of the fund)
complies/does not yet fully comply with the [methodological requirements set out in Delegated Regulation (EU) 2020/1818](https://eur-lex.europa.eu/legal-content/EN/ALL/?uri=CELEX%3A32020R1818#:~:text=CHAPTER%20II,THE%20BENCHMARK%20METHODOLOGY). In case the fund does not yet comply with all the requirements,
please, explain the reasons for it and describe the measures taken with a view to ensuring
compliance with the methodological requirements.`,
  de: `Die zugrunde liegenden Vermögenswerte von _________ (Name des Fonds) sind der Dekarbonisierung
der Wirtschaft verpflichtet und haben eine Verringerung der Kohlenstoffemissionen als
nachhaltiges Anlageziel. Das Ziel des Fonds richtet sich nach dem 1,5°C-Szenario mit
keinem oder nur geringem Überschreiten, das im Sonderbericht über die globale Erwärmung
um 1,5°C des Zwischenstaatlichen Ausschusses für Klimaänderungen (IPCC) als
Referenzszenario genannt wird. Da es keine Marktindizes gibt, die als Referenzwert für
_________ (Name des Fonds) genutzt werden können, enthält die vorliegende Erklärung eine
Beschreibung des kontinuierlichen Engagements zur Erreichung des Ziels der
Emissionsreduktion im Einklang mit den Vorgaben der SFDR. Daher liegt die gewichtete
durchschnittliche Treibhausgasintensität des Fonds (einschließlich Scope-1-, Scope-2- und
Scope-3-Emissionen) um _________ % unter der Treibhausgasintensität des investierbaren
Universums. Darüber hinaus erzielt der Fonds eine durchschnittliche jährliche
Treibhausgasintensitätsverringerung um _________ %. Schließlich wendet der Fonds bei
seinen Investitionsentscheidungen folgende Ausschlusskriterien an: _________.
Dementsprechend erfüllt das Finanzprodukt _________ (Name des Fonds) die
[methodischen Anforderungen der Delegierten Verordnung (EU) 2020/1818](https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=CELEX%3A32020R1818#:~:text=CHAPTER%20II,THE%20BENCHMARK%20METHODOLOGY)
entweder bereits oder noch nicht vollständig. Falls der Fonds die Anforderungen noch nicht
vollständig erfüllt, erläutern Sie bitte die Gründe hierfür und beschreiben Sie die Maßnahmen,
die ergriffen wurden, um die Einhaltung der methodischen Anforderungen sicherzustellen.`,
}

export function unAssignedVentureIds(fund: FundType): string[] {
  const ventureIdsInOtherObjective = (fund.sustainableObjectives || []).flatMap(
    (e: SfdrStrategyType) => e.ventureIds || [],
  )
  return (
    fund.ventureIds?.filter(
      (e: string) => !ventureIdsInOtherObjective.includes(e),
    ) ?? []
  )
}

export const sfdrTreeviewNames = {
  hasSustainableInvestmentWithEnvironmentalObjective: {
    en: 'It made **sustainable investments with an environmental objective**: %%ENV_OBJ%%%',
    de: 'Es wurden damit **nachhaltige Investitionen mit einem Umweltziel** getätigt: %%ENV_OBJ%%%',
  },
  hasEnvironmentalObjectiveEnvironmentallySustainableUnderEuTaxonomy: {
    en: 'in economic activities that qualify as environmentally sustainable under the EU Taxonomy',
    de: 'in Wirtschaftstätigkeiten, die nach der EU-Taxonomie als ökologisch nachhaltig einzustufen sind',
  },
  hasEnvironmentalObjectiveNonEnvironmentallySustainableUnderEuTaxonomy: {
    en: 'in economic activities that do not qualify as environmentally sustainable under the EU Taxonomy',
    de: 'in Wirtschaftstätigkeiten, die nach der EU-Taxonomie nicht als ökologisch nachhaltig einzustufen sind',
  },
  hasSustainableInvestmentWithSocialObjective: {
    en: 'It made **sustainable investments with a social objective**: %%SOC_OBJ%%%',
    de: 'Es wurden damit **nachhaltige Investitionen mit einem sozialen Ziel** getätigt: %%SOC_OBJ%%%',
  },
  hasEsCharacteristics: {
    en: 'It **promoted Environmental/Social (E/S) characteristics** and while it did not have as its objective a sustainable investment, it had a proportion of %%PROMOTED_ES%%% of sustainable investments.',
    de: 'Es wurden damit **ökologische/soziale Merkmale beworben** nd obwohl keine nachhaltigen Investitionen angestrebt wurden, enthielt es %%PROMOTED_ES%%% an nachhaltigen Investitionen.',
  },
  hasEsCharacteristicsAndEnvironmentalObjectiveSustainableUnderEuTaxonomy: {
    en: 'with an environmental objective in economic activities that qualify as environmentally sustainable under the EU Taxonomy',
    de: 'mit einem Umweltziel in Wirtschaftstätigkeiten, die nach der EU-Taxonomie als ökologisch nachhaltig einzustufen sind',
  },
  hasEsCharacteristicsAndEnvironmentalObjectiveNonSustainableUnderEuTaxonomy: {
    en: 'with an environmental objective in economic activities that do not qualify as environmentally sustainable under the EU Taxonomy',
    de: 'mit einem Umweltziel in Wirtschaftstätigkeiten, die nach der EU-Taxonomie nicht als ökologisch nachhaltig einzustufen sind',
  },
  hasEsCharacteristicsAndSocialObjective: {
    en: 'with a social objective',
    de: 'mit einem sozialen Ziel',
  },
  hasEsCharacteristicsWithoutSustainableInvestment: {
    en: 'It promoted E/S characteristics, but **did not make any sustainable investments**',
    de: 'Es wurden damit ökologische/soziale Merkmale beworben, aber keine nachhaltigen Investitionen getätigt',
  },
}

export const sfdrTreeviewNamesTargets = {
  hasSustainableInvestmentWithEnvironmentalObjective: {
    en: 'It will make a minimum of **sustainable investments with an environmental objective**',
    de: 'Es wird einen Mindestanteil an nachhaltigen Investitionen mit ökologischem Ziel haben',
  },
  hasEnvironmentalObjectiveEnvironmentallySustainableUnderEuTaxonomy: {
    en: 'in economic activities that qualify as environmentally sustainable under the EU Taxonomy',
    de: 'in wirtschaftlichen Tätigkeiten, die nach der EU-Taxonomie als ökologisch nachhaltig eingestuft werden',
  },
  hasEnvironmentalObjectiveNonEnvironmentallySustainableUnderEuTaxonomy: {
    en: 'in economic activities that do not qualify as environmentally sustainable under the EU Taxonomy',
    de: 'in wirtschaftlichen Tätigkeiten, die nach der EU-Taxonomie nicht als ökologisch nachhaltig eingestuft werden',
  },
  hasSustainableInvestmentWithSocialObjective: {
    en: 'It will make a minimum of **sustainable investments with a social objective**',
    de: 'Es wird einen Mindestanteil an nachhaltigen Investitionen mit sozialem Ziel haben',
  },
  hasEsCharacteristics: {
    en: 'It **promotes Environmental/Social (E/S) characteristics** and while it does not have as its objective a sustainable investment, it will have a minimum proportion sustainable investments of:',
    de: 'Es fördert ökologische und/oder soziale (E/S) Merkmale und hat zwar nicht das Ziel einer nachhaltigen Investition, wird aber einen Mindestanteil an nachhaltigen Investitionen haben',
  },
  hasEsCharacteristicsAndEnvironmentalObjectiveSustainableUnderEuTaxonomy: {
    en: 'with an environmental objective in economic activities that qualify as environmentally sustainable under the EU Taxonomy',
    de: 'mit einem Umweltziel in wirtschaftlichen Tätigkeiten, die nach der EU-Taxonomie als ökologisch nachhaltig eingestuft werden',
  },
  hasEsCharacteristicsAndEnvironmentalObjectiveNonSustainableUnderEuTaxonomy: {
    en: 'with an environmental objective in economic activities that do not qualify as environmentally sustainable under the EU Taxonomy',
    de: 'mit einem Umweltziel in wirtschaftlichen Tätigkeiten, die nach der EU-Taxonomie nicht als ökologisch nachhaltig eingestuft werden',
  },
  hasEsCharacteristicsAndSocialObjective: {
    en: 'with a social objective',
    de: 'mit einem sozialen Ziel',
  },
  hasEsCharacteristicsWithoutSustainableInvestment: {
    en: 'It promotes E/S characteristics, but **will not make any sustainable investments**',
    de: 'Es fördert E-/S-Merkmale, wird jedoch **keine nachhaltigen Investitionen tätigen**',
  },
}

export function getSectionIdsRecursive(sections) {
  return filter(
    sections.map((section) => {
      if (section?.disabled || section.disabled) {
        return null
      }
      if (section.sections) {
        return getSectionIdsRecursive(section.sections)
      } else {
        return section.id
      }
    }),
    (sectionId) => sectionId !== null,
  )
}

export function findSectionRecursive(sections: any[], targetId) {
  for (const section of sections) {
    if (section.id === targetId) {
      return section
    }
    if (section.sections?.length > 0) {
      const foundInSection = findSectionRecursive(section.sections, targetId)
      if (foundInSection) {
        return foundInSection
      }
    }
  }
  return null
}

export function flattenSections(sections: any[]) {
  const flatSections: any[] = []
  for (const section of sections) {
    flatSections.push(section)
    if (section.sections?.length > 0) {
      flatSections.push(...flattenSections(section.sections))
    }
  }
  return flatSections
}

export function isAnswered(val: any): number {
  if (val === undefined || val === null || val === '') return 0
  if (Array.isArray(val) && val.length === 0) return 0
  return 1
}

export const getNestedProperty = (obj, path, defaultValue = '') => {
  if (!path) {
    return ''
  }
  return path.split('.').reduce((o, p) => (o ? o[p] : defaultValue), obj)
}

export const getEnvironmentalMetricNames = (environmentalMetrics) => {
  let metricNames = []
  if (environmentalMetrics) {
    metricNames = metricNames.concat(
      environmentalMetrics.map((metric) => metric.name),
    )
  }
  return metricNames
}

export const getSocialMetricNames = (socialMetrics) => {
  let metricNames = []
  if (socialMetrics) {
    metricNames = metricNames.concat(socialMetrics.map((metric) => metric.name))
  }
  return metricNames
}
